import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getProductWithVariants,
  createProductVariant,
  updateProductVariant,
  deleteProductVariant,
} from '../../../features/product/services/productVariantService';

// ─── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchVariantsByProduct = createAsyncThunk(
  'productVariants/fetchByProduct',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await getProductWithVariants(productId);
      // Always map variants to camelCase for UI
      const payload = response?.payload || response;
      let variantsArr =
        payload?.Variants ||
        payload?.variants ||
        [];
      if (Array.isArray(variantsArr)) {
        variantsArr = variantsArr.map(variant => ({
          ...variant,
          sku: variant.sku || variant.SKU || '',
          salePrice: variant.salePrice ?? variant.SalePrice ?? 0,
          costPrice: variant.costPrice ?? variant.CostPrice ?? 0,
          stockQuantity: variant.stockQuantity ?? variant.StockQuantity ?? 0,
          availableStock: variant.availableStock ?? variant.AvailableStock ?? variant.stockQuantity ?? variant.StockQuantity ?? 0,
          variantName: variant.variantName || variant.VariantName || '',
          isSerializedProduct: variant.isSerializedProduct ?? variant.IsSerializedProduct ?? false,
          id: variant.id ?? variant.Id,
        }));
      }
      // Return with .variants for UI
      return { productId, data: { ...payload, variants: variantsArr } };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch variants');
    }
  }
);

export const addVariant = createAsyncThunk(
  'productVariants/add',
  async (variantData, { rejectWithValue }) => {
    try {
      const response = await createProductVariant(variantData);
      return response?.payload || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create variant');
    }
  }
);

export const editVariant = createAsyncThunk(
  'productVariants/edit',
  async ({ id, variantData }, { rejectWithValue }) => {
    try {
      const response = await updateProductVariant(id, variantData);
      return response?.payload || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update variant');
    }
  }
);

export const removeVariant = createAsyncThunk(
  'productVariants/remove',
  async ({ id, productId }, { rejectWithValue }) => {
    try {
      await deleteProductVariant(id);
      return { id, productId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete variant');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const productVariantSlice = createSlice({
  name: 'productVariants',
  initialState: {
    // Keyed by productId for fast lookup
    variantsByProduct: {},
    loading: false,
    submitting: false,
    error: null,
  },
  reducers: {
    clearVariantError: (state) => {
      state.error = null;
    },
    clearVariantsForProduct: (state, action) => {
      delete state.variantsByProduct[action.payload];
    },
  },
  extraReducers: (builder) => {
    // Fetch
    builder.addCase(fetchVariantsByProduct.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchVariantsByProduct.fulfilled, (state, action) => {
      state.loading = false;
      state.variantsByProduct[action.payload.productId] = action.payload.data;
    });
    builder.addCase(fetchVariantsByProduct.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Add
    builder.addCase(addVariant.pending, (state) => {
      state.submitting = true;
      state.error = null;
    });
    builder.addCase(addVariant.fulfilled, (state, action) => {
      state.submitting = false;
      const variant = action.payload;
      if (!variant) return;
      const pid = variant.productId;
      const current = state.variantsByProduct[pid];
      if (current) {
        current.variants = [...(current.variants || []), variant];
      }
    });
    builder.addCase(addVariant.rejected, (state, action) => {
      state.submitting = false;
      state.error = action.payload;
    });

    // Edit
    builder.addCase(editVariant.pending, (state) => {
      state.submitting = true;
      state.error = null;
    });
    builder.addCase(editVariant.fulfilled, (state, action) => {
      state.submitting = false;
      const updated = action.payload;
      if (!updated) return;
      const pid = updated.productId;
      const current = state.variantsByProduct[pid];
      if (current?.variants) {
        current.variants = current.variants.map((v) =>
          v.id === updated.id ? updated : v
        );
      }
    });
    builder.addCase(editVariant.rejected, (state, action) => {
      state.submitting = false;
      state.error = action.payload;
    });

    // Remove
    builder.addCase(removeVariant.pending, (state) => {
      state.submitting = true;
      state.error = null;
    });
    builder.addCase(removeVariant.fulfilled, (state, action) => {
      state.submitting = false;
      const { id, productId } = action.payload;
      const current = state.variantsByProduct[productId];
      if (current?.variants) {
        current.variants = current.variants.filter((v) => v.id !== id);
      }
    });
    builder.addCase(removeVariant.rejected, (state, action) => {
      state.submitting = false;
      state.error = action.payload;
    });
  },
});

export const { clearVariantError, clearVariantsForProduct } = productVariantSlice.actions;
export default productVariantSlice.reducer;
